/**
 * Environment configuration
 * Handles environment-specific variables and configuration
 */

/** Known app environment modes. Use --mode <mode> in Vite (e.g. vite build --mode staging). */
export type EnvironmentMode =
  | "development"
  | "staging"
  | "production"
  | "test";

interface Environment {
  /** Current mode (Vite's import.meta.env.MODE). */
  ENVIRONMENT: EnvironmentMode | string;
  /** Same as Vite's import.meta.env.DEV — true when MODE is "development". */
  DEV: boolean;
  /** Same as Vite's import.meta.env.PROD — true when MODE is "production". */
  PROD: boolean;
  /** True when MODE is "staging". */
  STAGING: boolean;
  /** True when MODE is "test". */
  TEST: boolean;
  /** True when MODE is development or test only. Staging is treated as prod-like (excluded). */
  isNonProduction: boolean;

  VITE_APP_NAME: string;
  API_URL: string;
  API_VERSION: string;
  LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error';
  ENABLE_CONSOLE_LOGS: boolean;
  SENTRY_DSN: string;
  SENTRY_ORG: string;
  SENTRY_PROJECT: string;
  SENTRY_AUTH_TOKEN: string;
  /** PostHog project API key. When empty, PostHog is disabled. */
  POSTHOG_KEY: string;
  /** PostHog host URL (e.g. EU or self-hosted). Falls back to US cloud when empty. */
  POSTHOG_HOST: string;
  /** Minutes before idle/absolute timeout to show session warning modal (default 1) */
  SESSION_WARNING_MINUTES: number;
  /** Secret used to derive the key for URL parameter encryption (obfuscation); same value everywhere for encode/decode */
  URL_ENCRYPTION_SECRET: string;
  /** Google reCAPTCHA Enterprise site key. When empty, recaptcha is disabled (dev-friendly). */
  RECAPTCHA_SITE_KEY: string;
  /** Social media URLs (for header/footer links). */
  SOCIAL_FACEBOOK_URL: string;
  SOCIAL_INSTAGRAM_URL: string;
  SOCIAL_TWITTER_URL: string;
  SOCIAL_LINKEDIN_URL: string;
  /** Support email (e.g. mailto links). Falls back to DPO email when unset. */
  SUPPORT_EMAIL: string;
  /** OZ Forensics on-screen debug overlay. Always false unless explicitly set to "true". */
  OZ_DEBUG: boolean;
  /**
   * When true, Book New Appointment shows purpose-of-visit confirmation and sends
   * `purposeOfJourneyId` on create. When false, legacy behaviour (no UI, no field).
   */
  ENABLE_PURPOSE_OF_VISIT_AT_BOOKING: boolean;
  /** When true, show passport prefill on signup + family application; calls BLS-LIVENESS POST /ocr/passport. */
  ENABLE_PASSPORT_MRZ_OCR: boolean;
  /** When true, show secondary "Use camera" for MRZ (Phase 2); until implemented, show disabled with coming-soon hint. */
  ENABLE_PASSPORT_MRZ_SCAN: boolean;

  PAYMENT_GATEWAY_FEATURE_ENABLED: boolean;
}

const mode = (import.meta.env.MODE || "development") as EnvironmentMode | string;

export const environment: Environment = {
  ENVIRONMENT: mode,
  DEV: import.meta.env.DEV ?? mode === "development",
  PROD: import.meta.env.PROD ?? mode === "production",
  STAGING: mode === "staging",
  TEST: mode === "test",
  isNonProduction: mode === "development" || mode === "test",
  VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
  API_URL: import.meta.env.VITE_API_URL,
  API_VERSION: import.meta.env.VITE_API_VERSION,
  LOG_LEVEL: (import.meta.env.VITE_LOG_LEVEL) as Environment['LOG_LEVEL'],
  ENABLE_CONSOLE_LOGS: import.meta.env.VITE_ENABLE_CONSOLE_LOGS,
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  SENTRY_ORG: import.meta.env.VITE_SENTRY_ORG,
  SENTRY_PROJECT: import.meta.env.VITE_SENTRY_PROJECT,
  SENTRY_AUTH_TOKEN: import.meta.env.VITE_SENTRY_AUTH_TOKEN,
  POSTHOG_KEY: (import.meta.env.VITE_POSTHOG_KEY as string) ?? "",
  POSTHOG_HOST: (import.meta.env.VITE_POSTHOG_HOST as string) ?? "",
  SESSION_WARNING_MINUTES: Number(import.meta.env.VITE_SESSION_WARNING_MINUTES),
  URL_ENCRYPTION_SECRET: import.meta.env.VITE_URL_ENCRYPTION_SECRET,
  RECAPTCHA_SITE_KEY: (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string) ?? "",
  SOCIAL_FACEBOOK_URL: (import.meta.env.VITE_SOCIAL_FACEBOOK_URL as string) ?? "",
  SOCIAL_INSTAGRAM_URL: (import.meta.env.VITE_SOCIAL_INSTAGRAM_URL as string) ?? "",
  SOCIAL_TWITTER_URL: (import.meta.env.VITE_SOCIAL_TWITTER_URL as string) ?? "",
  SOCIAL_LINKEDIN_URL: (import.meta.env.VITE_SOCIAL_LINKEDIN_URL as string) ?? "",
  SUPPORT_EMAIL: (import.meta.env.VITE_SUPPORT_EMAIL as string) ?? "dpo@blsinternational.com",
  OZ_DEBUG: import.meta.env.VITE_OZ_DEBUG === 'true',
  PAYMENT_GATEWAY_FEATURE_ENABLED:
    import.meta.env.VITE_PAYMENT_GATEWAY_FEATURE_ENABLED === 'true',
  ENABLE_PURPOSE_OF_VISIT_AT_BOOKING:
    import.meta.env.VITE_ENABLE_PURPOSE_OF_VISIT_AT_BOOKING === 'true',
  ENABLE_PASSPORT_MRZ_OCR: import.meta.env.VITE_ENABLE_PASSPORT_MRZ_OCR === 'true',
  ENABLE_PASSPORT_MRZ_SCAN: import.meta.env.VITE_ENABLE_PASSPORT_MRZ_SCAN === 'true',
};

// ──────────────────────────── VAS Display Configuration ────────────────────────────

/** Controls how VAS prices are displayed on service cards and in the order summary. */
export interface VasDisplayConfig {
  /**
   * Whether service cards (ServiceCard, PremiumServiceCard, CourierZoneCard, OutsideZoneCard)
   * show the **net** (excl. VAT) or **gross** (incl. VAT) price.
   */
  servicePriceDisplay: "net" | "gross";
  /** Label shown next to the price when displaying net (e.g. "excl. VAT"). Empty = hidden. */
  priceLabelNet: string;
  /** Label shown next to the price when displaying gross (e.g. "incl. VAT"). Empty = hidden. */
  priceLabelGross: string;
  /**
   * Order summary display mode:
   * - `gross_only`   — single total (incl. VAT), current behaviour.
   * - `net_and_vat`  — show net subtotal, VAT breakdown, then gross total.
   */
  orderSummaryVatDisplay: "gross_only" | "net_and_vat";
}

const parseServicePriceDisplay = (
  raw: string | undefined
): VasDisplayConfig["servicePriceDisplay"] =>
  raw === "gross" ? "gross" : "net";

const parseOrderSummaryVatDisplay = (
  raw: string | undefined
): VasDisplayConfig["orderSummaryVatDisplay"] =>
  raw === "net_and_vat" ? "net_and_vat" : "gross_only";

export const vasDisplayConfig: VasDisplayConfig = {
  servicePriceDisplay: parseServicePriceDisplay(
    import.meta.env.VITE_VAS_SERVICE_PRICE_DISPLAY
  ),
  priceLabelNet: import.meta.env.VITE_VAS_SERVICE_PRICE_LABEL_NET ?? "",
  priceLabelGross: import.meta.env.VITE_VAS_SERVICE_PRICE_LABEL_GROSS ?? "",
  orderSummaryVatDisplay: parseOrderSummaryVatDisplay(
    import.meta.env.VITE_ORDER_SUMMARY_VAT_DISPLAY
  ),
};
